/**
 * Emerald Books — Consolidation view: per-branch P&L + elimination column.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Building2, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { booksClient, errMsg } from './api';

const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const startOfYear = () => new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10);
const today = () => new Date().toISOString().slice(0, 10);

export const ConsolidationPage = () => {
  const [from, setFrom] = useState(startOfYear());
  const [to, setTo] = useState(today());
  const [includeElim, setIncludeElim] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await booksClient.get('/consolidation/group-pl', {
        params: { from, to, include_eliminations: includeElim },
      });
      setData(r.data);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const csv = () => {
    if (!data) return;
    const headers = ['Code', 'Account', 'Type',
      ...data.branches.map((b) => b.name),
      'Elimination', 'Group Total'];
    const rows = data.rows.map((r) => [
      r.code, r.name, r.type,
      ...data.branches.map((b) => r.by_branch[b.branch_id || 'unassigned'] ?? 0),
      r.elimination, r.group_total,
    ]);
    const content = [headers, ...rows].map((r) => r.map((c) =>
      typeof c === 'string' && c.includes(',') ? `"${c}"` : c).join(',')).join('\n');
    const blob = new Blob([content], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `group-pl-${from}-to-${to}.csv`; a.click();
  };

  const revenues = useMemo(() => (data?.rows || []).filter((r) => r.type === 'income'), [data]);
  const expenses = useMemo(() => (data?.rows || []).filter((r) => r.type === 'expense'), [data]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold text-[#0b1a2e]">Group Consolidation</h1>
          <p className="text-sm text-slate-500">Per-branch P&L · intercompany eliminations · group total</p>
        </div>
        <Button variant="outline" onClick={csv} disabled={!data}><Download className="w-4 h-4 mr-1" /> CSV</Button>
      </div>

      <div className="bg-white p-3 rounded border flex flex-wrap gap-2 items-center">
        <label className="text-xs text-slate-500">From</label>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" />
        <label className="text-xs text-slate-500">To</label>
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" />
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={includeElim} onChange={(e) => setIncludeElim(e.target.checked)} />
          Show eliminations
        </label>
        <Button onClick={load} className="bg-[#0f5c3d] hover:bg-[#0d4d33]" data-testid="consol-refresh-btn">Refresh</Button>
      </div>

      {loading && <Loader2 className="animate-spin mx-auto text-slate-400" />}

      {data && (
        <div className="bg-white rounded-lg border overflow-auto" data-testid="consol-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 sticky left-0 bg-slate-50">Account</th>
                {data.branches.map((b) => (
                  <th key={b.branch_id || 'unassigned'} className="text-right px-3 py-2 whitespace-nowrap">
                    <div className="inline-flex items-center gap-1 justify-end"><Building2 className="w-3 h-3 opacity-50" />{b.name}</div>
                  </th>
                ))}
                {includeElim && <th className="text-right px-3 py-2">Elimination</th>}
                <th className="text-right px-3 py-2 bg-emerald-50 text-emerald-800">Group</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-slate-50">
                <td colSpan={data.branches.length + 2 + (includeElim ? 1 : 0)} className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-emerald-800">Revenue</td>
              </tr>
              {revenues.map((r) => (
                <tr key={r.account_id} className="border-t hover:bg-slate-50/50">
                  <td className="px-3 py-1.5 sticky left-0 bg-white"><span className="font-mono text-xs text-slate-400 mr-2">{r.code}</span>{r.name}</td>
                  {data.branches.map((b) => (
                    <td key={b.branch_id || 'un'} className="text-right px-3 py-1.5 font-mono text-xs">
                      {money(r.by_branch[b.branch_id || 'unassigned'] || 0)}
                    </td>
                  ))}
                  {includeElim && <td className="text-right px-3 py-1.5 font-mono text-xs text-rose-600">{money(r.elimination)}</td>}
                  <td className="text-right px-3 py-1.5 font-mono text-xs bg-emerald-50/40 font-semibold">{money(r.group_total)}</td>
                </tr>
              ))}
              <tr className="bg-slate-50">
                <td colSpan={data.branches.length + 2 + (includeElim ? 1 : 0)} className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-rose-800">Expenses</td>
              </tr>
              {expenses.map((r) => (
                <tr key={r.account_id} className="border-t hover:bg-slate-50/50">
                  <td className="px-3 py-1.5 sticky left-0 bg-white"><span className="font-mono text-xs text-slate-400 mr-2">{r.code}</span>{r.name}</td>
                  {data.branches.map((b) => (
                    <td key={b.branch_id || 'un'} className="text-right px-3 py-1.5 font-mono text-xs">
                      {money(r.by_branch[b.branch_id || 'unassigned'] || 0)}
                    </td>
                  ))}
                  {includeElim && <td className="text-right px-3 py-1.5 font-mono text-xs text-rose-600">{money(r.elimination)}</td>}
                  <td className="text-right px-3 py-1.5 font-mono text-xs bg-emerald-50/40 font-semibold">{money(r.group_total)}</td>
                </tr>
              ))}
              <tr className="border-t-2 bg-[#0b1a2e] text-white font-semibold">
                <td className="px-3 py-2 sticky left-0 bg-[#0b1a2e]">Net Profit (Group)</td>
                {data.branches.map((b) => (
                  <td key={b.branch_id || 'un'} className="text-right px-3 py-2 font-mono text-xs">
                    {money(data.totals_by_branch[b.branch_id || 'unassigned'] || 0)}
                  </td>
                ))}
                {includeElim && <td className="text-right px-3 py-2 font-mono text-xs">{money(data.elimination_total)}</td>}
                <td className="text-right px-3 py-2 font-mono text-emerald-300">{money(data.group_total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ConsolidationPage;
